import React, { useCallback, memo, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CMSection, CMSubSection, CMField, CMTable } from '../add-edit.types';
import SectionFieldToolbar from '../section-field-toolbar';
import SectionFieldIcons from '../section-field-icons';
import { MAX_SUBSECTIONS_LIMIT } from '../utils/constants';
import {
  FieldList,
  DropdownConfig,
  CheckboxConfig,
  RadioConfig,
  TextBoxConfig,
  DatePickerConfig,
  CommentBoxConfig,
  ImageConfig,
  StatusConfig,
  TableColumnsEditor,
  SourceConfig,
  CalculationConfig,
} from '../../../../../Components/Controls/cm-control-config';
import type {
  FieldItem,
  DropdownConfigValue,
  CheckboxConfigValue,
  RadioConfigValue,
  TextBoxConfigValue,
  DatePickerConfigValue,
  CommentBoxConfigValue,
  ImageConfigValue,
  StatusConfigValue,
  CalculationConfigValue,
} from '../../../../../Components/Controls/cm-control-config';
import type { SourceConfigValue } from '../../../../../Components/Controls/cm-control-config/types';
import LibraryTitleField from '../../../../../Components/Controls/library-title-field';
import type { SectionConfigBodyProps } from './section-config-body.types';
import { AddIcon, DeleteIcon, RearrangeIcon } from '../../../../../bombiix-icon';
import { NewInput } from '../../../../../Components/PIM/common/NewInput';
import { isFullWidthFieldType, isWidthTotalOverLimit } from '../utils/system-canvas-cm.utils';
import PanelFlagsRow from './panel-flags-row';
import FieldSetupCard from './field-setup-card';
import { getFieldSetupAccess } from './field-setup-access';
import { CanvasConfig, TimePickerConfig } from '../../../../../Components/Controls/cm-control-config';
import type { CanvasConfigValue, TimePickerConfigValue } from '../../../../../Components/Controls/cm-control-config';
import TitleVisibilityButton from '../../../../../Components/Controls/title-visibility-button';

function parseWidthPct(value: string | undefined): number {
  if (!value || typeof value !== 'string') return 0;
  const trimmed = value.trim();
  const match = /^(\d+(?:\.\d+)?)\s*%?$/.exec(trimmed);
  return match ? Number(match[1]) : 0;
}

const FieldEditorRenderer: React.FC<{
  editingFieldPath: { subIndex: number; fieldIndex: number } | null;
  subIndex: number;
  field: CMField;
  siblingFields: CMField[];
  toFieldItem: (f: CMField, index: number) => any;
  getFieldApplyCallback: (subIndex: number, fieldIndex: number) => (nextCfg: unknown) => void;
  onClose: () => void;
}> = memo(({ editingFieldPath, subIndex, field, siblingFields, toFieldItem, getFieldApplyCallback, onClose }) => {
  const [widthError, setWidthError] = useState<string | null>(null);

  if (!editingFieldPath || editingFieldPath.subIndex !== subIndex) return null;

  const item = useMemo(() => {
    return toFieldItem(field, editingFieldPath.fieldIndex);
  }, [field, editingFieldPath.fieldIndex, toFieldItem]);

  const apply = useMemo(() => {
    return getFieldApplyCallback(subIndex, editingFieldPath.fieldIndex);
  }, [subIndex, editingFieldPath.fieldIndex, getFieldApplyCallback]);

  if (!item || !apply) return null;

  const currentFieldIndex = editingFieldPath.fieldIndex;
  const totalOtherPct = siblingFields.reduce((sum, ff, i) => (i === currentFieldIndex || isFullWidthFieldType(ff.type) ? sum : sum + parseWidthPct(ff.width)), 0);

  useEffect(() => {
    const currentPct = parseWidthPct(field.width);
    if (currentPct > 0 && isWidthTotalOverLimit(totalOtherPct + currentPct)) {
      setWidthError('Total width must not exceed 100%');
    } else {
      setWidthError(null);
    }
  }, [field.width, totalOtherPct]);

  const handleWidthChange = useCallback(
    (w: string) => {
      const newPct = parseWidthPct(w || '');
      const total = totalOtherPct + newPct;
      if (newPct > 0 && isWidthTotalOverLimit(total)) {
        setWidthError('Total width must not exceed 100%');
        return;
      }
      setWidthError(null);
      apply({ ...(item.config as Record<string, unknown>), width: w || '' });
    },
    [totalOtherPct, apply, item.config],
  );

  const fieldEditorKey = `field_${subIndex}_${editingFieldPath.fieldIndex}_${item.type}`;

  return (
    <FieldSetupCard
      key={fieldEditorKey}
      fieldType={item.type}
      title={(item.config as { title?: string }).title ?? ''}
      onTitleChange={(title) => apply({ ...(item.config as Record<string, unknown>), title })}
      onClose={onClose}
      {...(!isFullWidthFieldType(item.type) ? { width: field.width ?? '', onWidthChange: handleWidthChange, widthError } : {})}
      access={getFieldSetupAccess(field, item)}
      onAccessChange={(patch) => apply({ ...(item.config as Record<string, unknown>), ...patch })}
    >
      {(() => {
        switch (item.type) {
          case 'dropdown':
            return <DropdownConfig value={item.config as DropdownConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'checkbox':
            return <CheckboxConfig value={item.config as CheckboxConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'radio':
            return <RadioConfig value={item.config as RadioConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'text':
            return <TextBoxConfig value={item.config as TextBoxConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'date':
            return <DatePickerConfig value={item.config as DatePickerConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'comment':
            return <CommentBoxConfig value={item.config as CommentBoxConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'status':
            return <StatusConfig value={item.config as StatusConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'image':
            return <ImageConfig value={item.config as ImageConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'source':
            return <SourceConfig value={item.config as SourceConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'calculation':
            return <CalculationConfig value={item.config as CalculationConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'canvas':
            return <CanvasConfig value={item.config as CanvasConfigValue} onChange={apply} showLibraryTitle={false} hideTitle />;
          case 'time':
            return <TimePickerConfig value={item.config as TimePickerConfigValue} onChange={apply} />;
          default:
            return null;
        }
      })()}
    </FieldSetupCard>
  );
});

FieldEditorRenderer.displayName = 'FieldEditorRenderer';

const SectionConfigBody: React.FC<SectionConfigBodyProps> = ({
  activeSectionIndex,
  effectiveSections,
  sections,
  tempSections,
  setTempSections,
  setOpenRenderedSections,
  setOpenRenderedSubSections,
  requestDeleteSubSection,
  editingFieldPath,
  setEditingFieldPath,
  editingTablePath,
  setEditingTablePath,
  toFieldItem,
  applyConfigToField,
  setToolbarSections,
  canShowAddSubSectionButton,
  addSubSectionToActive,
  showLibraryTitle = true,
}) => {
  const { t } = useTranslation(['custom-module-system-canvas', 'common']);
  const dragSubSectionIndexRef = React.useRef<number | null>(null);
  const scrollContainerRef = React.useRef<HTMLElement | null>(null);

  const replaceActiveSection = useCallback(
    (sectionData: CMSection) => {
      if (activeSectionIndex === null) return;
      setTempSections((prev) => {
        const base = prev ?? sections;
        return base.map((section, index) => (index === activeSectionIndex ? { ...section, content: sectionData.content as CMSubSection[] } : section));
      });
    },
    [activeSectionIndex, sections, setTempSections],
  );

  const handleSectionSelect = useCallback(
    (pulled: unknown) => {
      const fetchedSection = pulled as CMSection | undefined;
      if (fetchedSection) {
        replaceActiveSection({ ...fetchedSection });
      }
    },
    [replaceActiveSection],
  );

  const getSubSectionSelectHandler = useCallback(
    (subIndex: number) => (pulled: unknown) => {
      const fetchedSubSection = pulled as CMSubSection | undefined;
      if (fetchedSubSection && activeSectionIndex !== null) {
        setTempSections((prev) =>
          (prev ?? sections).map((section: CMSection, sectionIndex: number) => {
            if (sectionIndex !== activeSectionIndex) return section;
            const nextContent: CMSubSection[] = (section.content as CMSubSection[]).map((subsection: CMSubSection, subsectionIndex: number) => {
              if (subsectionIndex !== subIndex) return subsection;
              return { ...fetchedSubSection };
            });
            return { ...section, content: nextContent };
          }),
        );
      }
    },
    [activeSectionIndex, sections, setTempSections],
  );

  const handleReorderSubSections = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (activeSectionIndex === null) return;
      setTempSections((prev) => {
        const base = prev ?? sections;
        return base.map((section: CMSection, sectionIndex: number) => {
          if (sectionIndex !== activeSectionIndex) return section;
          const subSections = (section.content as CMSubSection[]) || [];
          const nextSubSections = [...subSections];
          const [movedItem] = nextSubSections.splice(fromIndex, 1);
          if (movedItem) {
            nextSubSections.splice(toIndex, 0, movedItem);
          }
          return { ...section, content: nextSubSections };
        });
      });
      setOpenRenderedSubSections((prev) => {
        const updated = new Set(prev);
        const oldKey = `${activeSectionIndex}_${fromIndex}`;
        const newKey = `${activeSectionIndex}_${toIndex}`;
        if (updated.has(oldKey)) {
          updated.delete(oldKey);
          updated.add(newKey);
        }
        prev.forEach((key) => {
          if (key.startsWith(`${activeSectionIndex}_`)) {
            const keyParts = key.split('_');
            if (keyParts.length === 2) {
              const subIdx = parseInt(keyParts[1] || '0', 10);
              if (!isNaN(subIdx) && subIdx !== fromIndex && subIdx !== toIndex) {
                if (fromIndex < subIdx && subIdx <= toIndex) {
                  updated.delete(key);
                  updated.add(`${activeSectionIndex}_${subIdx - 1}`);
                } else if (toIndex <= subIdx && subIdx < fromIndex) {
                  updated.delete(key);
                  updated.add(`${activeSectionIndex}_${subIdx + 1}`);
                }
              }
            }
          }
        });
        return Array.from(updated);
      });
    },
    [activeSectionIndex, sections, setTempSections, setOpenRenderedSubSections],
  );

  const mousePositionRef = React.useRef<{ x: number; y: number } | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);

  const performAutoScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !mousePositionRef.current) {
      animationFrameRef.current = null;
      return;
    }

    const rect = container.getBoundingClientRect();
    const mouseY = mousePositionRef.current.y;
    const scrollThreshold = 100;
    const maxScrollSpeed = 15;
    const minScrollSpeed = 5;

    const distanceFromTop = mouseY - rect.top;
    const distanceFromBottom = rect.bottom - mouseY;

    let scrollAmount = 0;

    if (distanceFromTop < scrollThreshold && container.scrollTop > 0) {
      const factor = Math.max(0, (scrollThreshold - distanceFromTop) / scrollThreshold);
      scrollAmount = -(minScrollSpeed + (maxScrollSpeed - minScrollSpeed) * factor);
    } else if (distanceFromBottom < scrollThreshold && container.scrollTop < container.scrollHeight - container.clientHeight) {
      const factor = Math.max(0, (scrollThreshold - distanceFromBottom) / scrollThreshold);
      scrollAmount = minScrollSpeed + (maxScrollSpeed - minScrollSpeed) * factor;
    }

    if (scrollAmount !== 0) {
      container.scrollTop += scrollAmount;
      animationFrameRef.current = requestAnimationFrame(performAutoScroll);
    } else {
      animationFrameRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(
    (container: HTMLElement) => {
      if (animationFrameRef.current !== null) return;

      scrollContainerRef.current = container;
      animationFrameRef.current = requestAnimationFrame(performAutoScroll);
    },
    [performAutoScroll],
  );

  const updateMousePosition = useCallback(
    (e: React.DragEvent) => {
      mousePositionRef.current = { x: e.clientX, y: e.clientY };

      if (animationFrameRef.current === null && scrollContainerRef.current) {
        startAutoScroll(scrollContainerRef.current);
      }
    },
    [startAutoScroll],
  );

  const stopAutoScroll = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    scrollContainerRef.current = null;
    mousePositionRef.current = null;
  }, []);

  React.useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const FIELD_DRAG_TYPE = 'application/x-field-drag';

  const handleCopyFieldToSubSection = useCallback(
    (sourceSubIndex: number, sourceFieldIndex: number, targetSubIndex: number) => {
      if (activeSectionIndex === null) return;
      const base = tempSections ?? sections;
      const section = base[activeSectionIndex];
      if (!section?.content) return;
      const subSections = section.content as CMSubSection[];
      const sourceSub = subSections[sourceSubIndex];
      if (!sourceSub?.content) return;
      const sourceContent = sourceSub.content as Array<CMField | CMTable>;
      const sourceField = sourceContent[sourceFieldIndex];
      if (!sourceField || (sourceField as CMTable).columns !== undefined) return;
      const targetSub = subSections[targetSubIndex];
      if (!targetSub?.content) return;
      const targetContent = targetSub.content as Array<CMField | CMTable>;
      const targetFields = targetContent.filter((c) => !('columns' in (c as CMTable))) as CMField[];
      const targetTables = targetContent.filter((c) => 'columns' in (c as CMTable));
      const clonedField = JSON.parse(JSON.stringify(sourceField)) as CMField;
      const newContent: Array<CMField | CMTable> = [...targetFields, clonedField, ...targetTables];
      setTempSections((prev) =>
        (prev ?? sections).map((sec: CMSection, si: number) => {
          if (si !== activeSectionIndex) return sec;
          return {
            ...sec,
            content: (sec.content as CMSubSection[]).map((sub: CMSubSection, ssi: number) => (ssi !== targetSubIndex ? sub : { ...sub, content: newContent })),
          };
        }),
      );
    },
    [activeSectionIndex, sections, tempSections, setTempSections],
  );

  const handleDragOverSubSection = useCallback(
    (e: React.DragEvent, _subIndex: number, canDrag: boolean) => {
      e.preventDefault();
      if (canDrag) updateMousePosition(e);
    },
    [updateMousePosition],
  );

  const handleDropSubSection = useCallback(
    (e: React.DragEvent, subIndex: number, canDrag: boolean) => {
      e.preventDefault();
      const fieldDragRaw = e.dataTransfer.getData(FIELD_DRAG_TYPE);
      if (fieldDragRaw) {
        try {
          const { subIndex: fromSub, fieldIndex: fromField } = JSON.parse(fieldDragRaw) as {
            subIndex: number;
            fieldIndex: number;
          };
          if (typeof fromSub === 'number' && typeof fromField === 'number' && fromSub !== subIndex) {
            e.stopPropagation();
            handleCopyFieldToSubSection(fromSub, fromField, subIndex);
          }
        } catch {}
        return;
      }
      const from = dragSubSectionIndexRef.current;
      const to = subIndex;
      if (from !== null && to !== null && from !== to && canDrag) {
        handleReorderSubSections(from, to);
      }
      dragSubSectionIndexRef.current = null;
      stopAutoScroll();
    },
    [handleCopyFieldToSubSection, handleReorderSubSections, stopAutoScroll],
  );

  const handleDragEndSubSection = useCallback(() => {
    dragSubSectionIndexRef.current = null;
    stopAutoScroll();
  }, [stopAutoScroll]);

  const handleDragStartSubSection = useCallback((e: React.DragEvent, subIndex: number) => {
    dragSubSectionIndexRef.current = subIndex;
    const container = e.currentTarget.closest('.offcanvas-body') as HTMLElement;
    if (container) {
      scrollContainerRef.current = container;
      mousePositionRef.current = { x: e.clientX, y: e.clientY };
    }
    try {
      e.dataTransfer.setData('text/plain', String(subIndex));
    } catch {}
  }, []);

  const subSectionsMemo = React.useMemo(() => {
    if (activeSectionIndex === null) return [];
    return ((effectiveSections[activeSectionIndex]?.content as CMSubSection[]) || [])
      .map((sub: CMSubSection, subIndex: number) => ({ sub, subIndex }))
      .filter(({ sub }) => sub && 'content' in sub && !('columns' in (sub as unknown as CMTable)));
  }, [activeSectionIndex, effectiveSections]);

  const canDragSubSectionMemo = React.useMemo(() => subSectionsMemo.length > 1, [subSectionsMemo.length]);

  const handleSubSectionAccordionChange = useCallback(
    (subIndex: number, checked: boolean) => {
      if (activeSectionIndex === null) return;
      const subKey = `${activeSectionIndex}_${subIndex}`;
      setTempSections((prev) =>
        (prev ?? sections).map((section: CMSection, sectionIndex: number) => {
          if (sectionIndex !== activeSectionIndex) return section;
          const nextContent: CMSubSection[] = (section.content as CMSubSection[]).map((subsection: CMSubSection, subsectionIndex: number) => {
            if (subsectionIndex !== subIndex) return subsection;
            return { ...subsection, isAccordion: checked };
          });
          return { ...section, content: nextContent };
        }),
      );
      setOpenRenderedSubSections((prev) => {
        if (checked) {
          return prev.includes(subKey) ? prev : [...prev, subKey];
        }
        return prev.filter((key) => key !== subKey);
      });
    },
    [activeSectionIndex, sections, setTempSections, setOpenRenderedSubSections],
  );

  const handleSubSectionTitleChange = useCallback(
    (subIndex: number, val: string) => {
      if (activeSectionIndex === null) return;
      setTempSections((prev) =>
        (prev ?? sections).map((section: CMSection, sectionIndex: number) => {
          if (sectionIndex !== activeSectionIndex) return section;
          const nextContent: CMSubSection[] = (section.content as CMSubSection[]).map((subsection: CMSubSection, subsectionIndex: number) => {
            if (subsectionIndex !== subIndex) return subsection;
            return { ...subsection, title: val };
          });
          return { ...section, content: nextContent };
        }),
      );
    },
    [activeSectionIndex, sections, setTempSections],
  );

  const handleSubSectionVisibleToSupplierChange = useCallback(
    (subIndex: number, checked: boolean) => {
      if (activeSectionIndex === null) return;
      setTempSections((prev) =>
        (prev ?? sections).map((section: CMSection, sectionIndex: number) => {
          if (sectionIndex !== activeSectionIndex) return section;
          const nextContent: CMSubSection[] = (section.content as CMSubSection[]).map((subsection: CMSubSection, subsectionIndex: number) => {
            if (subsectionIndex !== subIndex) return subsection;
            return { ...subsection, isVisibleToSupplier: checked };
          });
          return { ...section, content: nextContent };
        }),
      );
    },
    [activeSectionIndex, sections, setTempSections],
  );

  const handleSubSectionFieldDelete = useCallback(
    (subIndex: number, itemIndex: number) => {
      if (activeSectionIndex === null) return;
      setTempSections((prev) =>
        (prev ?? sections).map((section: CMSection, sectionIndex: number) => {
          if (sectionIndex !== activeSectionIndex) return section;
          const nextContent: CMSubSection[] = (section.content as CMSubSection[]).map((subsection: CMSubSection, subsectionIndex: number) => {
            if (subsectionIndex !== subIndex) return subsection;
            const inner: Array<CMField | CMTable> = Array.isArray(subsection.content) ? [...subsection.content] : [];
            const filtered = inner.filter((_, contentIndex: number) => contentIndex !== itemIndex);
            return { ...subsection, content: filtered };
          });
          return { ...section, content: nextContent };
        }),
      );
    },
    [activeSectionIndex, sections, setTempSections],
  );

  const handleSubSectionListChange = useCallback(
    (subIndex: number, nextItems: FieldItem[]) => {
      if (activeSectionIndex === null) return;
      setTempSections((prev) =>
        (prev ?? sections).map((section: CMSection, sectionIndex: number) => {
          if (sectionIndex !== activeSectionIndex) return section;
          const nextContent = (section.content as CMSubSection[]).map((subsection: CMSubSection, subsectionIndex: number) => {
            if (!(subsection && 'content' in subsection) || subsectionIndex !== subIndex) return subsection;
            const currentContent = subsection.content as Array<CMField | CMTable>;
            const fieldsOnly = currentContent.filter((contentItem) => contentItem && !('columns' in (contentItem as CMTable))) as CMField[];
            const tablesOnly = currentContent.filter((contentItem) => (contentItem as CMTable) && 'columns' in (contentItem as CMTable)) as CMTable[];

            const reorderedFields = nextItems
              .map((item) => {
                const originalIndex = Number(item.id.replace('f_', ''));
                const originalField = fieldsOnly[originalIndex];
                if (!originalField) return null;
                return { ...originalField, isAccordion: !!item.convertToAccordion } as CMField;
              })
              .filter((field): field is CMField => field !== null);

            const updatedContent: Array<CMField | CMTable> = [...reorderedFields, ...tablesOnly];
            return { ...subsection, content: updatedContent };
          });
          return { ...section, content: nextContent as unknown as CMSubSection[] };
        }),
      );
    },
    [activeSectionIndex, sections, setTempSections],
  );

  const handleSubSectionFieldEdit = useCallback(
    (subIndex: number, item: FieldItem | null) => {
      if (item) {
        setEditingFieldPath({ subIndex, fieldIndex: Number(item.id.replace('f_', '')) });
      } else {
        setEditingFieldPath(null);
      }
    },
    [setEditingFieldPath],
  );

  const getFieldApplyCallback = useCallback(
    (subIndex: number, fieldIndex: number) => {
      return (nextCfg: unknown) => {
        setTempSections((prev) =>
          (prev ?? sections).map((section: CMSection, sectionIndex: number) => {
            if (sectionIndex !== activeSectionIndex) return section;
            const nextContent = (section.content as CMSubSection[]).map((subsection: CMSubSection, subsectionIndex: number) => {
              if (!(subsection && 'content' in subsection) || subsectionIndex !== subIndex) return subsection;
              const updatedFields = (subsection.content as Array<CMField | CMTable>).map((contentItem, idx) => {
                if (!((contentItem as CMField) && 'type' in (contentItem as CMField))) return contentItem;
                if (idx !== fieldIndex) return contentItem;
                return applyConfigToField(contentItem as CMField, nextCfg);
              });
              return { ...subsection, content: updatedFields };
            });
            return { ...section, content: nextContent as unknown as CMSubSection[] };
          }),
        );
      };
    },
    [activeSectionIndex, sections, setTempSections, applyConfigToField],
  );

  const activeSection = activeSectionIndex !== null ? effectiveSections[activeSectionIndex] : undefined;

  const updateActiveSection = (patch: Partial<CMSection>) => {
    if (activeSectionIndex === null) return;
    setTempSections((prev) =>
      (prev ?? sections).map((section: CMSection, sectionIndex: number) => (sectionIndex === activeSectionIndex ? { ...section, ...patch } : section)),
    );
  };

  const handleSectionAccordionChange = (checked: boolean) => {
    if (activeSectionIndex === null) return;
    updateActiveSection({ isAccordion: checked });
    setOpenRenderedSections((prev) => {
      if (checked) {
        return prev.includes(activeSectionIndex) ? prev : [...prev, activeSectionIndex];
      }
      return prev.filter((idx) => idx !== activeSectionIndex);
    });
  };

  const handleSectionTitleChange = (title: string) => updateActiveSection({ title });

  const closeFieldEditor = () => setEditingFieldPath(null);

  /**
   * Shows or hides a sub-section's title on the rendered form.
   * @param subIndex - Index of the sub-section in the active section.
   * @param isShown - Whether the title is shown.
   */
  const handleSubSectionTitleShownChange = (subIndex: number, isShown: boolean) => {
    if (activeSectionIndex === null) return;
    setTempSections((prev) =>
      (prev ?? sections).map((section: CMSection, sectionIndex: number) => {
        if (sectionIndex !== activeSectionIndex) return section;
        const nextContent: CMSubSection[] = (section.content as CMSubSection[]).map((subsection: CMSubSection, subsectionIndex: number) =>
          subsectionIndex === subIndex ? { ...subsection, showSubSectionTitle: isShown } : subsection,
        );
        return { ...section, content: nextContent };
      }),
    );
  };

  return (
    <>
      <div className="cm-panel-block">
        <PanelFlagsRow
          idPrefix="section"
          isAccordion={!!activeSection?.isAccordion}
          onAccordionChange={handleSectionAccordionChange}
          isVisibleToSupplier={activeSection?.isVisibleToSupplier ?? true}
          onVisibleToSupplierChange={(checked) => updateActiveSection({ isVisibleToSupplier: checked })}
        />
        {showLibraryTitle ? (
          <LibraryTitleField
            value={activeSection?.title ?? ''}
            onChange={handleSectionTitleChange}
            onPull={handleSectionSelect}
            controlType="section"
            content={activeSection ?? null}
            placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SECTION_TITLE')}
            isTitleShown={activeSection?.showSectionTitle !== false}
            onTitleShownChange={(isShown) => updateActiveSection({ showSectionTitle: isShown })}
          />
        ) : (
          <NewInput
            id="sectionTitleInput"
            placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SECTION_TITLE')}
            value={activeSection?.title ?? ''}
            onChange={(event) => handleSectionTitleChange(event.target.value)}
            endAdornment={
              <TitleVisibilityButton
                isShown={activeSection?.showSectionTitle !== false}
                onChange={(isShown) => updateActiveSection({ showSectionTitle: isShown })}
              />
            }
          />
        )}
      </div>

      {activeSectionIndex !== null &&
        subSectionsMemo.map(({ sub, subIndex }) => {
          const fields = (sub.content as Array<CMField | CMTable>).filter(
            (contentItem) => contentItem && !('columns' in (contentItem as CMTable)),
          ) as CMField[];
          const items: FieldItem[] = fields.map((field: CMField, fieldIndex: number) => toFieldItem(field, fieldIndex)).filter(Boolean) as FieldItem[];
          let subsectionForceDisableAllTools = false;
          if (editingTablePath && editingTablePath.subIndex === subIndex) {
            subsectionForceDisableAllTools = true;
          } else if (editingFieldPath && editingFieldPath.subIndex === subIndex && activeSectionIndex !== null) {
            const currentSections = tempSections ?? sections;
            const currentSub = (currentSections[activeSectionIndex]?.content as CMSubSection[])?.[subIndex];
            const fld = (currentSub?.content as Array<CMField | CMTable>)?.[editingFieldPath.fieldIndex];
            if (fld && !('columns' in (fld as CMTable)) && (fld as CMField).type === 'comment') {
              subsectionForceDisableAllTools = true;
            }
          }
          return (
            <div
              key={`sub_${subIndex}`}
              className="cm-panel-subsection"
              onDragOver={(e) => handleDragOverSubSection(e, subIndex, canDragSubSectionMemo)}
              onDrop={(e) => handleDropSubSection(e, subIndex, canDragSubSectionMemo)}
            >
              <div className="cm-panel-block">
                <PanelFlagsRow
                  idPrefix={`sub-section-${subIndex}`}
                  isAccordion={!!sub.isAccordion}
                  onAccordionChange={(checked) => handleSubSectionAccordionChange(subIndex, checked)}
                  isVisibleToSupplier={sub.isVisibleToSupplier !== false}
                  onVisibleToSupplierChange={(checked) => handleSubSectionVisibleToSupplierChange(subIndex, checked)}
                  leading={
                    canDragSubSectionMemo ? (
                      <span
                        className="cm-panel-flags__handle"
                        draggable
                        onDragStart={(e) => handleDragStartSubSection(e, subIndex)}
                        onDragEnd={handleDragEndSubSection}
                      >
                        <RearrangeIcon size={PANEL_ICON_SIZE} />
                      </span>
                    ) : null
                  }
                  trailing={
                    canDragSubSectionMemo ? (
                      <button
                        type="button"
                        className="cm-panel-flags__delete"
                        aria-label={t('COMMON.DELETE')}
                        onClick={() => requestDeleteSubSection(activeSectionIndex as number, subIndex)}
                      >
                        <DeleteIcon size={PANEL_ICON_SIZE} />
                      </button>
                    ) : null
                  }
                />
                {showLibraryTitle ? (
                  <LibraryTitleField
                    value={sub.title ?? ''}
                    onChange={(value: string) => handleSubSectionTitleChange(subIndex, value)}
                    onPull={getSubSectionSelectHandler(subIndex)}
                    controlType="subSection"
                    content={sub}
                    placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SUB_SECTION_TITLE')}
                    isTitleShown={sub.showSubSectionTitle !== false}
                    onTitleShownChange={(isShown) => handleSubSectionTitleShownChange(subIndex, isShown)}
                  />
                ) : (
                  <NewInput
                    placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SUB_SECTION_TITLE')}
                    value={sub.title ?? ''}
                    onChange={(e) => handleSubSectionTitleChange(subIndex, e.target.value)}
                    endAdornment={
                      <TitleVisibilityButton
                        isShown={sub.showSubSectionTitle !== false}
                        onChange={(isShown) => handleSubSectionTitleShownChange(subIndex, isShown)}
                      />
                    }
                  />
                )}
              </div>
              <SectionFieldIcons mode="subsection" subSection={sub} onDelete={(itemIndex: number) => handleSubSectionFieldDelete(subIndex, itemIndex)} />
              <SectionFieldToolbar
                sections={effectiveSections}
                activeSectionIndex={activeSectionIndex}
                setSections={setToolbarSections}
                forceDisableAll={subsectionForceDisableAllTools}
                targetSubIndex={subIndex}
                showCommentBtn={true}
              />
              {(() => {
                const tableIndex = (sub.content as Array<CMField | CMTable>).findIndex(
                  (contentItem) => (contentItem as CMTable) && 'columns' in (contentItem as CMTable),
                );
                if (tableIndex === -1) return null;
                const table = (sub.content as Array<CMField | CMTable>)[tableIndex] as CMTable;
                const setTable = (updater: (table: CMTable) => CMTable) =>
                  setTempSections((prev) =>
                    (prev ?? sections).map((section: CMSection, sectionIndex: number) => {
                      if (sectionIndex !== activeSectionIndex) return section;
                      const nextContent = (section.content as CMSubSection[]).map((subsection: CMSubSection, subsectionIndex: number) => {
                        if (subsectionIndex !== subIndex) return subsection;
                        const subCopy: CMSubSection = { ...(subsection as CMSubSection) };
                        const inner: Array<CMField | CMTable> = Array.isArray(subCopy?.content) ? [...subCopy.content] : [];
                        const tableIndexInContent = inner.findIndex((contentItem) => 'columns' in (contentItem as CMTable));
                        if (tableIndexInContent === -1) return subsection;
                        const updatedTable = updater({ ...(inner[tableIndexInContent] as CMTable) });
                        inner[tableIndexInContent] = updatedTable;
                        subCopy.content = inner;
                        return subCopy;
                      });
                      return { ...section, content: nextContent as unknown as CMSubSection[] };
                    }),
                  );
                return (
                  <TableColumnsEditor
                    table={table}
                    setTable={setTable}
                    subIndex={subIndex}
                    editingTablePath={editingTablePath}
                    setEditingTablePath={setEditingTablePath}
                    toFieldItem={toFieldItem}
                    applyConfigToField={applyConfigToField}
                    showLibraryTitle={showLibraryTitle}
                  />
                );
              })()}
              {items.length > 0 && (
                <FieldList
                  fields={items}
                  onChange={(nextItems) => handleSubSectionListChange(subIndex, nextItems)}
                  onEdit={(item) => handleSubSectionFieldEdit(subIndex, item)}
                  onDragStart={(fieldIndex, e) => {
                    try {
                      e.dataTransfer.setData(FIELD_DRAG_TYPE, JSON.stringify({ subIndex, fieldIndex }));
                      e.dataTransfer.effectAllowed = 'copy';
                    } catch {}
                  }}
                  {...(editingFieldPath?.subIndex === subIndex && editingFieldPath?.fieldIndex !== undefined && { selectedIndex: editingFieldPath.fieldIndex })}
                  showLibraryButton={showLibraryTitle}
                  onLibraryPull={(fieldIndex, config) => getFieldApplyCallback(subIndex, fieldIndex)(config)}
                  renderEditor={(fieldIndex) => {
                    const field = fields[fieldIndex];
                    if (!field || !editingFieldPath || editingFieldPath.subIndex !== subIndex) return null;
                    return (
                      <FieldEditorRenderer
                        editingFieldPath={editingFieldPath}
                        subIndex={subIndex}
                        field={field}
                        siblingFields={fields}
                        toFieldItem={toFieldItem}
                        getFieldApplyCallback={getFieldApplyCallback}
                        onClose={closeFieldEditor}
                      />
                    );
                  }}
                />
              )}
            </div>
          );
        })}
      {activeSectionIndex !== null &&
        (() => {
          if (!activeSection) return null;
          const contentArray: CMSubSection[] = (activeSection.content as CMSubSection[]) || [];
          const subsCount = contentArray.filter(
            (subsection) => subsection && 'content' in subsection && !('columns' in (subsection as unknown as CMTable)),
          ).length;
          const isLimitReached = subsCount >= MAX_SUBSECTIONS_LIMIT;
          const shouldShowButton = canShowAddSubSectionButton || isLimitReached;
          if (!shouldShowButton) return null;
          return (
            <div className="cm-subsection-add">
              <button type="button" className="cm-subsection-add__link" onClick={addSubSectionToActive} disabled={isLimitReached}>
                <AddIcon size={ADD_SUB_SECTION_ICON_SIZE} />
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.ADD_NEW_SUB_SECTION')}
              </button>
              {isLimitReached && <div className="text-danger small mt-1">{t('CUSTOM_MODULE.VALIDATION.MAX_SUB_SECTIONS_LIMIT')}</div>}
            </div>
          );
        })()}
    </>
  );
};

export default memo(SectionConfigBody);

const PANEL_ICON_SIZE = 20;

const ADD_SUB_SECTION_ICON_SIZE = 16;
